import { useIntl } from 'react-intl';

import { OrderWordsInteraction } from './OrderWordsInteraction.tsx';
import { CardMarkdown } from './CardMarkdown.tsx';
import { StudyChoiceOption } from './StudyChoiceOption.tsx';
import { StudyReference } from './StudyReference.tsx';
import { useStudyController } from '../lib/use-study-controller.ts';
import {
  canSubmitStudyAnswer,
  studyOptionKeyboardAction,
} from '../lib/study-keyboard.ts';
import { cn } from '../lib/utils.ts';
import { Alert, AlertDescription } from './ui/alert.tsx';
import { Button } from './ui/button.tsx';
import { Card, CardContent } from './ui/card.tsx';
import { Input } from './ui/input.tsx';
import type { DeckScope } from '../../shared/deck.ts';

type StudyInterfaceProps = {
  deckId: string;
  deckSlug: string;
  scope?: DeckScope;
  onReviewRecorded?: () => void | Promise<void>;
};

export function StudyInterface({
  deckId,
  deckSlug,
  scope,
  onReviewRecorded,
}: StudyInterfaceProps) {
  const intl = useIntl();
  const controller = useStudyController({ deckId, onReviewRecorded, scope });
  const {
    card,
    canonicalOrderWords,
    continueStudy,
    error,
    feedback,
    inputRef,
    loadNext,
    optionStatus,
    orderStatus,
    orderWorkspace,
    selectedOptionIds,
    setOrderWorkspace,
    setSelectedOptionIds,
    setTextValue,
    submit,
    submitting,
    textValue,
    toggleOption,
  } = controller;
  function feedbackIcon(status: 'correct' | 'incorrect', className?: string) {
    return (
      <span
        className={cn(
          'inline-grid size-6 shrink-0 place-items-center rounded-full border-2 border-current font-extrabold leading-none',
          status === 'correct' ? 'text-success' : 'text-destructive',
          className,
        )}
      >
        {status === 'correct' ? '✓' : '×'}
      </span>
    );
  }

  return (
    <section>
      <Card
        className={cn(
          'border border-border bg-card text-card-foreground shadow-[0_8px_18px_rgb(23_22_20_/_0.06)] [--card-spacing:1.25rem] max-[520px]:[--card-spacing:1rem]',
          feedback?.result === 'correct' && 'border-success bg-success-surface',
          feedback?.result === 'incorrect' &&
            'border-destructive bg-error-surface',
        )}
      >
        <CardContent className="grid gap-6">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription className="grid gap-3">
                <p>{intl.formatMessage({ id: 'web.study.error' })}</p>
                <Button
                  className="min-h-11 w-fit font-semibold"
                  variant="secondary"
                  onClick={() => void loadNext(card?.id)}
                  type="button"
                >
                  {intl.formatMessage({ id: 'web.study.retry' })}
                </Button>
              </AlertDescription>
            </Alert>
          ) : card === undefined ? (
            <p className="text-muted-foreground">
              {intl.formatMessage({ id: 'web.study.loading' })}
            </p>
          ) : card === null ? (
            <p className="text-muted-foreground">
              {intl.formatMessage({ id: 'web.study.empty' })}
            </p>
          ) : (
            <form
              className="grid gap-5"
              onSubmit={(event) => {
                event.preventDefault();
                void submit();
              }}
            >
              <h2 className="m-0 font-sans text-2xl leading-tight font-normal">
                <CardMarkdown source={card.prompt} />
              </h2>
              {card.type === 'text-input' ? (
                <div className="grid gap-2.5">
                  <div
                    className={cn(
                      'flex items-center gap-3',
                      feedback?.result === 'incorrect' && 'text-destructive',
                    )}
                  >
                    <Input
                      autoComplete="off"
                      className={cn(
                        'flex-1 bg-background text-base',
                        feedback?.result === 'incorrect' &&
                          'border-destructive bg-error-surface text-destructive disabled:bg-error-surface disabled:text-destructive disabled:opacity-100',
                      )}
                      disabled={Boolean(feedback) || submitting}
                      onChange={(event) => setTextValue(event.target.value)}
                      ref={inputRef}
                      value={textValue}
                    />
                    {feedback ? feedbackIcon(feedback.result) : null}
                  </div>
                  {feedback?.result === 'incorrect' &&
                  feedback.correctAnswer ? (
                    <div className="flex items-center gap-3 text-success">
                      <span className="flex-1 rounded-lg border border-success bg-[var(--success-surface)] p-3 text-base">
                        {feedback.correctAnswer}
                      </span>
                      {feedbackIcon('correct')}
                    </div>
                  ) : null}
                </div>
              ) : card.type === 'order-words' ? (
                <>
                  <p
                    className="text-base text-muted-foreground"
                    id={`order-word-instructions-${card.id}`}
                  >
                    {intl.formatMessage({
                      id: 'web.study.orderWordsInstructions',
                    })}
                  </p>
                  <OrderWordsInteraction
                    canonicalWorkspace={canonicalOrderWords()}
                    disabled={Boolean(feedback) || submitting}
                    items={card.items}
                    onWorkspaceChange={setOrderWorkspace}
                    workspace={orderWorkspace}
                    workspaceItemStatus={
                      feedback?.result === 'correct' ? undefined : orderStatus
                    }
                  />
                </>
              ) : (
                <div className="grid gap-2.5">
                  {card.options.map((option, index) => {
                    const status = optionStatus(option.id);
                    return (
                      <StudyChoiceOption
                        disabled={Boolean(feedback) || submitting}
                        index={index}
                        key={option.id}
                        onClick={() => toggleOption(option.id)}
                        onKeyDown={(event) => {
                          if (
                            studyOptionKeyboardAction(
                              event,
                              selectedOptionIds.includes(option.id),
                            ) === 'submit'
                          ) {
                            event.preventDefault();
                            void submit();
                          }
                        }}
                        option={option}
                        selected={selectedOptionIds.includes(option.id)}
                        status={feedback ? status : undefined}
                      />
                    );
                  })}
                </div>
              )}
              {feedback ? (
                <div className="grid gap-3">
                  {feedback.reference ? (
                    <StudyReference
                      deckSlug={deckSlug}
                      reference={feedback.reference}
                    />
                  ) : null}
                  <Button
                    className="min-h-11 w-fit font-semibold"
                    variant="default"
                    onClick={continueStudy}
                    type="button"
                  >
                    {intl.formatMessage({ id: 'web.study.continue' })}
                  </Button>
                </div>
              ) : (
                <Button
                  className="min-h-11 w-fit font-semibold"
                  variant="default"
                  disabled={
                    submitting ||
                    !canSubmitStudyAnswer(
                      card,
                      textValue,
                      selectedOptionIds,
                      orderWorkspace.length,
                    )
                  }
                  type="submit"
                >
                  {intl.formatMessage({ id: 'web.study.submit' })}
                </Button>
              )}
            </form>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
